// Shared per-team setup: the state, data-loading, validation, persistence and
// field UIs for the three team-configuration steps (Team / Current work /
// Standup). Used by BOTH the onboarding wizard (steps 3-5) and the in-app
// "Create a team" modal so the two flows can never drift apart again.
//
// The hosts own their own chrome (the wizard's two-pane shell + rail stepper;
// the modal's antd dialog + footer) and their own step headings. This module
// owns everything team-specific underneath.
import React, { useEffect, useState } from 'react';
import { Select, Switch, message } from 'antd';
import {
  getSlackChannels,
  getJiraProjects,
  getJiraBoards,
  getBoardSprints,
  getJqlCount,
  previewMembers,
  createTeam,
  updateTeam,
  saveAiStandup,
} from './teamsApi';
import { listGithubRepos, getGithubConnection } from '../github/githubApi';
// The field UIs use the wizard's `tpo__` classes; pull them in wherever the
// fields render (e.g. inside the modal).
import '../onBoarding/v21/onboarding.css';

export const COLORS = ['#5039BB', '#1D9E75', '#D85A30', '#D4537E', '#185FA5', '#BA7517'];

export const initials = (s) =>
  (s || '?').split(/[\s@.]+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

// IANA timezone list for the dropdown. Prefer the browser's canonical list;
// fall back to a small curated set on older browsers.
export const TIMEZONES = (() => {
  try {
    if (window.Intl && typeof window.Intl.supportedValuesOf === 'function') {
      return window.Intl.supportedValuesOf('timeZone');
    }
  } catch (e) {
    /* fall through */
  }
  return [
    'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
    'America/Los_Angeles', 'America/Sao_Paulo', 'Europe/London', 'Europe/Berlin',
    'Europe/Paris', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore',
    'Asia/Shanghai', 'Asia/Tokyo', 'Australia/Sydney',
  ];
})();

export const InfoIcon = ({ stroke = '#5a6fb0' }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9.2" stroke={stroke} strokeWidth="1.6" />
    <path d="M12 11v5M12 7.8v.01" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

// ---------------------------------------------------------------------------
// Hook: all per-team state + loads + validation + savers.
// ---------------------------------------------------------------------------
export function useTeamSetup(wId) {
  // Team
  const [name, setName] = useState('');
  const [source, setSource] = useState('slack'); // slack | jira
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState(null);
  const [members, setMembers] = useState([]);
  const [memberSync, setMemberSync] = useState(true);
  const [teamId, setTeamId] = useState(null);

  // Resources — project(s) and repo(s) this team works in.
  const [jiraProjectList, setJiraProjectList] = useState([]);
  const [repoList, setRepoList] = useState([]);
  const [selectedJiraKeys, setSelectedJiraKeys] = useState([]);
  const [selectedRepoNames, setSelectedRepoNames] = useState([]);

  // Current work — no default; the user must actively pick a mode.
  const [workType, setWorkType] = useState('');
  const [jql, setJql] = useState('project = SP AND statusCategory != Done');
  const [boardList, setBoardList] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [sprintList, setSprintList] = useState([]);
  const [sprintsLoading, setSprintsLoading] = useState(false);
  const [selectedSprintId, setSelectedSprintId] = useState(null);
  const [jqlCount, setJqlCount] = useState(null); // null = not checked
  const [jqlChecking, setJqlChecking] = useState(false);

  // Standup / defaults
  const [timezone, setTimezone] = useState('');
  const [standupEnabled, setStandupEnabled] = useState(true);
  const standupTime = '08:00';
  const [reviewBeforePost, setReviewBeforePost] = useState(true);
  const [askWhenUnsure, setAskWhenUnsure] = useState(true);

  // Connection status (inferred from the option loads below).
  const [slackConnected, setSlackConnected] = useState(false);
  const [jiraConnected, setJiraConnected] = useState(false);
  const [githubConnected, setGithubConnected] = useState(false);

  // Load pickable options + infer which integrations are connected.
  useEffect(() => {
    if (!wId) return;
    getSlackChannels(wId)
      .then((res) => {
        if (res.data && res.data.success) {
          setChannels(res.data.channels || []);
          setSlackConnected(true);
        }
      })
      .catch(() => {});
    getJiraProjects(wId)
      .then((res) => {
        if (res.data && res.data.success) {
          setJiraProjectList(res.data.projects || []);
          setJiraConnected(true);
        }
      })
      .catch(() => {});
    listGithubRepos(wId)
      .then((res) => {
        if (res.data && res.data.success && res.data.connected) {
          setRepoList(res.data.repos || []);
          setGithubConnected(true);
        }
      })
      .catch(() => {});
    getGithubConnection(wId)
      .then((res) => {
        if (res.data && res.data.success && res.data.connected) setGithubConnected(true);
      })
      .catch(() => {});
  }, [wId]);

  // Auto-detect the timezone so the Standup step comes pre-filled.
  useEffect(() => {
    try {
      const tz = window.Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) setTimezone(tz);
    } catch (e) {
      /* leave blank */
    }
  }, []);

  const selectedChannel = channels.find((c) => c.id === channelId);

  const onChannel = (id) => {
    setChannelId(id);
    previewMembers(wId, 'slack_channel', id)
      .then((res) => setMembers((res.data && res.data.members) || []))
      .catch(() => {});
  };

  const ensureBoards = () => {
    if (boardList.length || boardsLoading || !jiraConnected) return;
    setBoardsLoading(true);
    getJiraBoards(wId)
      .then((res) => setBoardList((res.data && res.data.boards) || []))
      .catch(() => {})
      .finally(() => setBoardsLoading(false));
  };

  const onBoard = (id) => {
    setSelectedBoardId(id);
    setSelectedSprintId(null);
    setSprintList([]);
    if (workType === 'sprint') {
      setSprintsLoading(true);
      getBoardSprints(wId, id)
        .then((res) => setSprintList((res.data && res.data.sprints) || []))
        .catch(() => {})
        .finally(() => setSprintsLoading(false));
    }
  };

  const checkJql = () => {
    if (!jql.trim()) return;
    setJqlChecking(true);
    setJqlCount(null);
    getJqlCount(wId, jql)
      .then((res) => {
        if (res.data && res.data.success) setJqlCount(res.data.total);
        else message.error('Could not validate that JQL');
      })
      .catch(() => message.error('Could not validate that JQL'))
      .finally(() => setJqlChecking(false));
  };

  const currentWorkLabel = () => {
    if (workType === 'sprint') {
      const s = sprintList.find((x) => String(x.id) === String(selectedSprintId));
      return s ? `Active sprint — ${s.name}` : 'Active sprint';
    }
    if (workType === 'board') {
      const b = boardList.find((x) => String(x.id) === String(selectedBoardId));
      return b ? `Board — ${b.name}` : 'A board';
    }
    return jql;
  };

  // Validation per step.
  const teamValid = !!name.trim() && !!channelId;
  const currentWorkValid =
    selectedJiraKeys.length > 0 &&
    !!workType &&
    !(workType === 'sprint' && !selectedSprintId) &&
    !(workType === 'board' && !selectedBoardId) &&
    !(workType === 'jql' && !jql.trim());

  // Savers. Each throws on failure; the caller surfaces the message. They take
  // an optional explicit id so a fast multi-step flow doesn't race setTeamId.
  const saveTeamStep = async () => {
    if (teamId) return teamId;
    const res = await createTeam(wId, {
      name: name.trim(),
      source: { type: 'slack_channel', id: channelId },
      default_channel: selectedChannel
        ? { id: selectedChannel.id, name: selectedChannel.name }
        : undefined,
      keepInSync: memberSync,
    });
    if (!res.data || !res.data.success) throw new Error('create failed');
    const id = res.data.team._id;
    setTeamId(id);
    return id;
  };

  const saveCurrentWork = async (id = teamId) => {
    const value =
      workType === 'sprint'
        ? String(selectedSprintId)
        : workType === 'board'
        ? String(selectedBoardId)
        : jql;
    await updateTeam(wId, id, {
      jira_projects: selectedJiraKeys.map((key) => {
        const p = jiraProjectList.find((x) => x.key === key) || {};
        return { key, name: p.name || key };
      }),
      repos: selectedRepoNames.map((full) => {
        const r = repoList.find((x) => x.full_name === full) || {};
        return { owner: r.owner, repo: r.repo };
      }),
      current_work: { type: workType, value },
    });
  };

  const saveStandup = async (id = teamId) => {
    // Team-level delivery defaults stay on the team. The standup itself is no
    // longer stored on the team (standup_config is gone) — it now lives as an
    // engine:'ai' check-in row in the teamsync collections, created/updated
    // idempotently below.
    await updateTeam(wId, id, {
      defaults: { standup_channel: selectedChannel, timezone, keep_in_sync: memberSync },
    });
    await saveAiStandup(wId, {
      engine: 'ai',
      standuptype: 'ai_standup',
      name: 'Automated Standup',
      standupscheduleType: 'recurring',
      createInstance: standupEnabled,
      workspace_team_id: id,
      time_at: standupTime,
      // Weekdays by default (matches the mockup); editable later in config.
      weekdays: '1,2,3,4,5',
      days: [1, 2, 3, 4, 5],
      timezone_type: 'custom_timezone',
      timezone,
      selectedChannel: selectedChannel && selectedChannel.id,
      reportChannels: selectedChannel ? [selectedChannel] : [],
      report_to: 'channel',
      report_type: 'after_wait_time',
      wait_time: 5400,
      // The engine reads the team roster directly; no per-member list needed.
      selectedMembers: [],
      questions: [],
      ai_config: {
        review_before_post: reviewBeforePost,
        ask_when_unsure: askWhenUnsure,
        read_from: ['jira', 'github', 'slack'],
      },
    });
  };

  return {
    // team
    name, setName, source, setSource, channels, channelId, members, memberSync,
    setMemberSync, teamId, selectedChannel, onChannel,
    // resources
    jiraProjectList, repoList, selectedJiraKeys, setSelectedJiraKeys,
    selectedRepoNames, setSelectedRepoNames,
    // current work
    workType, setWorkType, jql, setJql, boardList, boardsLoading,
    selectedBoardId, setSelectedBoardId, sprintList, sprintsLoading,
    selectedSprintId, setSelectedSprintId, jqlCount, setJqlCount, jqlChecking,
    ensureBoards, onBoard, checkJql, currentWorkLabel,
    // standup
    timezone, setTimezone, standupEnabled, setStandupEnabled, standupTime,
    reviewBeforePost, setReviewBeforePost, askWhenUnsure, setAskWhenUnsure,
    // connections
    slackConnected, setSlackConnected, jiraConnected, setJiraConnected,
    githubConnected, setGithubConnected,
    // validation + savers
    teamValid, currentWorkValid, saveTeamStep, saveCurrentWork, saveStandup,
  };
}

// ---------------------------------------------------------------------------
// Field UIs. Hosts render their own eyebrow/title/desc, then drop these in.
// ---------------------------------------------------------------------------
export function TeamFields({ t }) {
  return (
    <>
      <div className="tpo__label">Team name</div>
      <input
        className="tpo__input"
        value={t.name}
        onChange={(e) => t.setName(e.target.value)}
        placeholder="e.g. Platform"
      />

      <div className="tpo__label">Pull members from</div>
      <div className="tpo__seg">
        <button
          className={'tpo__seg-opt' + (t.source === 'slack' ? ' tpo__seg-opt--active' : '')}
          onClick={() => t.setSource('slack')}
        >
          A Slack channel
        </button>
        <button
          className={'tpo__seg-opt' + (t.source === 'jira' ? ' tpo__seg-opt--active' : '')}
          onClick={() => t.setSource('jira')}
        >
          A Jira project
        </button>
      </div>

      {t.source === 'jira' ? (
        <div className="tpo__callout tpo__callout--blue" style={{ marginTop: 14 }}>
          <InfoIcon />
          <span>Pulling members from a Jira project is coming soon. Use a Slack channel for now.</span>
        </div>
      ) : (
        <>
          <div style={{ marginTop: 12 }}>
            <Select
              showSearch
              style={{ width: '100%' }}
              placeholder="Select a channel"
              value={t.channelId || undefined}
              onChange={t.onChannel}
              optionFilterProp="children"
              size="large"
            >
              {t.channels.map((c) => (
                <Select.Option key={c.id} value={c.id}>#{c.name}</Select.Option>
              ))}
            </Select>
          </div>
          {t.channelId && (
            <>
              <div className="tpo__source">
                <span className="tpo__conn-tile tpo__conn-tile--slack" style={{ width: 38, height: 38, fontSize: 18 }}>#</span>
                <div>
                  <div className="tpo__source-name">#{t.selectedChannel && t.selectedChannel.name}</div>
                  <div className="tpo__source-sub">
                    {t.members.length} {t.members.length === 1 ? 'person' : 'people'} in this channel
                  </div>
                </div>
              </div>
              <div className="tpo__avatars">
                {t.members.slice(0, 6).map((m, i) => (
                  <span
                    key={i}
                    className="tpo__avatar"
                    style={{ background: COLORS[i % COLORS.length] }}
                    title={m.name || m.email || 'Unknown member'}
                  >
                    {initials(m.name || m.email)}
                  </span>
                ))}
                <span className="tpo__avatars-note">
                  {t.members.length} {t.members.length === 1 ? 'person' : 'people'} will be added
                </span>
              </div>
            </>
          )}
        </>
      )}

      <div className="tpo__toggle" style={{ marginTop: 18 }}>
        <div>
          <div className="tpo__toggle-title">Keep members in sync</div>
          <div className="tpo__toggle-sub">
            When someone joins {t.selectedChannel ? `#${t.selectedChannel.name}` : 'the channel'}, I
            ask before adding them — never silently.
          </div>
        </div>
        <Switch checked={t.memberSync} onChange={t.setMemberSync} />
      </div>
    </>
  );
}

export function CurrentWorkFields({ t }) {
  return (
    <>
      <div className="tpo__label">Jira project(s) this team works in</div>
      <Select
        mode="multiple"
        showSearch
        style={{ width: '100%' }}
        size="large"
        placeholder={t.jiraProjectList.length ? 'Select project(s)' : 'Connect Jira to pick projects'}
        value={t.selectedJiraKeys}
        onChange={t.setSelectedJiraKeys}
        optionFilterProp="children"
      >
        {t.jiraProjectList.map((p) => (
          <Select.Option key={p.key} value={p.key}>
            {p.name ? `${p.name} (${p.key})` : p.key}
          </Select.Option>
        ))}
      </Select>

      <div className="tpo__label">
        GitHub repo(s) for standup signal{' '}
        <span style={{ color: '#8a93a6', fontWeight: 400 }}>· optional</span>
      </div>
      <Select
        mode="multiple"
        showSearch
        style={{ width: '100%' }}
        size="large"
        placeholder={t.repoList.length ? 'Select repo(s)' : 'Connect GitHub to pick repos'}
        value={t.selectedRepoNames}
        onChange={t.setSelectedRepoNames}
        optionFilterProp="children"
      >
        {t.repoList.map((r) => (
          <Select.Option key={r.full_name} value={r.full_name}>
            {r.full_name}
          </Select.Option>
        ))}
      </Select>

      <div className="tpo__label" style={{ marginTop: 18 }}>
        Which issues count as current work?
      </div>
      {[
        { k: 'sprint', t: 'Active sprint', s: 'You run sprints. I track the current sprint and roll over automatically when it changes.' },
        { k: 'board', t: 'A board', s: 'Track everything on a board, no sprints.' },
        { k: 'jql', t: 'A filter or JQL', s: "Define current work yourself. Best for continuous delivery, where there's no sprint to bound it." },
      ].map((o) => (
        <div
          key={o.k}
          className={'tpo__radio' + (t.workType === o.k ? ' tpo__radio--active' : '')}
          onClick={() => {
            t.setWorkType(o.k);
            if (o.k === 'sprint' || o.k === 'board') t.ensureBoards();
          }}
        >
          <span className="tpo__radio-dot" />
          <div className="tpo__radio-body">
            <div className="tpo__radio-title">{o.t}</div>
            <div className="tpo__radio-sub">{o.s}</div>

            {o.k === 'board' && t.workType === 'board' && (
              <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 12 }}>
                <Select
                  showSearch
                  loading={t.boardsLoading}
                  style={{ width: '100%' }}
                  size="large"
                  placeholder={t.boardList.length ? 'Select a board' : 'No boards found — connect Jira'}
                  value={t.selectedBoardId || undefined}
                  onChange={t.setSelectedBoardId}
                  optionFilterProp="children"
                >
                  {t.boardList.map((b) => (
                    <Select.Option key={b.id} value={b.id}>
                      {b.name}{b.type ? ` · ${b.type}` : ''}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            {o.k === 'sprint' && t.workType === 'sprint' && (
              <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 12 }}>
                <Select
                  showSearch
                  loading={t.boardsLoading}
                  style={{ width: '100%' }}
                  size="large"
                  placeholder={t.boardList.length ? 'Select a board' : 'No boards found — connect Jira'}
                  value={t.selectedBoardId || undefined}
                  onChange={t.onBoard}
                  optionFilterProp="children"
                >
                  {t.boardList.map((b) => (
                    <Select.Option key={b.id} value={b.id}>
                      {b.name}{b.type ? ` · ${b.type}` : ''}
                    </Select.Option>
                  ))}
                </Select>
                {t.selectedBoardId && (
                  <Select
                    showSearch
                    loading={t.sprintsLoading}
                    style={{ width: '100%', marginTop: 10 }}
                    size="large"
                    placeholder={t.sprintList.length ? 'Select a sprint' : (t.sprintsLoading ? 'Loading sprints…' : 'No open sprints on this board')}
                    value={t.selectedSprintId || undefined}
                    onChange={t.setSelectedSprintId}
                    optionFilterProp="children"
                  >
                    {t.sprintList.map((s) => (
                      <Select.Option key={s.id} value={s.id}>
                        {s.name}{s.state ? ` · ${s.state}` : ''}
                      </Select.Option>
                    ))}
                  </Select>
                )}
              </div>
            )}

            {o.k === 'jql' && t.workType === 'jql' && (
              <div onClick={(e) => e.stopPropagation()}>
                <input
                  className="tpo__input tpo__input--mono"
                  style={{ marginTop: 12 }}
                  value={t.jql}
                  onChange={(e) => { t.setJql(e.target.value); t.setJqlCount(null); }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                  <button
                    className="tpo__conn-btn"
                    onClick={t.checkJql}
                    disabled={t.jqlChecking || !t.jql.trim()}
                  >
                    {t.jqlChecking ? 'Checking…' : 'Check matches'}
                  </button>
                  {t.jqlCount !== null && (
                    <span className="tpo__match" style={{ margin: 0 }}>
                      ✓ {t.jqlCount} issue{t.jqlCount === 1 ? '' : 's'} match right now
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
      <div className="tpo__callout tpo__callout--blue">
        <InfoIcon />
        <span>
          Using sprints? Pick Active sprint and you're done. Shipping continuously? A filter
          keeps "current" meaningful without a sprint to close.
        </span>
      </div>
    </>
  );
}

export function StandupFields({ t }) {
  return (
    <>
      <div className="tpo__label">Timezone</div>
      <Select
        showSearch
        style={{ width: '100%' }}
        size="large"
        placeholder="Select a timezone"
        value={t.timezone || undefined}
        onChange={t.setTimezone}
        optionFilterProp="children"
      >
        {(TIMEZONES.includes(t.timezone) || !t.timezone
          ? TIMEZONES
          : [t.timezone, ...TIMEZONES]
        ).map((tz) => (
          <Select.Option key={tz} value={tz}>{tz}</Select.Option>
        ))}
      </Select>

      <div className="tpo__toggle tpo__toggle--hl" style={{ marginTop: 16 }}>
        <div>
          <div className="tpo__toggle-title">Turn on the automated standup</div>
          <div className="tpo__toggle-sub">
            You can skip this and turn it on later from the team's settings — nothing else in
            setup depends on it.
          </div>
        </div>
        <Switch checked={t.standupEnabled} onChange={t.setStandupEnabled} />
      </div>

      <div style={{ opacity: t.standupEnabled ? 1 : 0.45, pointerEvents: t.standupEnabled ? 'auto' : 'none' }}>
        <div className="tpo__group">
          <div className="tpo__grow">
            <div className="tpo__grow-text">
              <span className="tpo__grow-label">Schedule</span>
              <span className="tpo__grow-value">Weekdays at {t.standupTime}{t.timezone ? `, ${t.timezone}` : ', your workspace timezone'}</span>
            </div>
          </div>
          <div className="tpo__grow">
            <div className="tpo__grow-text">
              <span className="tpo__grow-label">Posts to</span>
              <span className="tpo__grow-value">{t.selectedChannel ? `#${t.selectedChannel.name}` : '—'}</span>
            </div>
          </div>
          <div className="tpo__grow">
            <div className="tpo__grow-text">
              <span className="tpo__grow-label">Written from</span>
              <span className="tpo__grow-value">Your current work in Jira, plus GitHub and Slack activity</span>
            </div>
          </div>
        </div>
        <div className="tpo__toggle">
          <div>
            <div className="tpo__toggle-title">Let people review before it posts</div>
            <div className="tpo__toggle-sub">
              Before the standup goes to the channel, I DM each person their drafted update to
              approve or edit. If they don't respond in time, I post the prepared one.
            </div>
          </div>
          <Switch checked={t.reviewBeforePost} onChange={t.setReviewBeforePost} />
        </div>
        <div className="tpo__toggle">
          <div>
            <div className="tpo__toggle-title">Ask a person directly when I'm unsure</div>
            <div className="tpo__toggle-sub">
              If activity doesn't tell me what someone is working on, I ask them rather than guess.
            </div>
          </div>
          <Switch checked={t.askWhenUnsure} onChange={t.setAskWhenUnsure} />
        </div>
      </div>
    </>
  );
}
